import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { v as bvVar } from 'bv';

import getClassNameFromType from './button/helpers/get_class_name_from_type';

const getBaseClass = (noClass, inverse) => {
  if (noClass) {
    return '';
  }

  return inverse ? 'bvs-button-alternative' : 'bvs-button';
};

const getButtonElement = (href, to) => {
  let element;
  if (href) {
    element = 'a';
  } else if (to) {
    element = Link;
  } else {
    element = 'button';
  }
  return element;
};

const Button = ({
  className: classNameProp,
  invisible,
  primary,
  secondary,
  info,
  warning,
  danger,
  disabled,
  isRound,
  default: isDefault,
  href,
  to,
  ref,
  onClick,
  dangerouslySetInnerHTML,
  target,
  rel,
  type,
  label,
  active,
  children,
  loading,
  noClass,
  inverse,
}) => {
  const className = classnames(classNameProp, getBaseClass(noClass, inverse), {
    'is-invisible': invisible,
    'is-primary': primary,
    'is-secondary': secondary,
    'is-info': info,
    'is-warning': warning,
    'is-danger': danger,
    disabled,
    'is-round': isRound,
    'is-default': isDefault,
    active,
  }, getClassNameFromType(type));

  const elementProps = {
    className,
    onClick,
    href,
    to,
    target,
    rel,
    type,
    ref,
    ...(dangerouslySetInnerHTML ? {
      dangerouslySetInnerHTML,
    } : {}),
  };

  const Element = getButtonElement(href, to);

  // Children cannot be used together with dangerouslySetInnerHTML
  if (dangerouslySetInnerHTML) {
    return <Element {...elementProps} />;
  }

  return (
    <Element
      {...elementProps}
    >
      {label || children}
      {loading && <span className="bvs-spinner is-light loading" />}
    </Element>
  );
};

Button.propTypes = {
  onClick: PropTypes.func,
  dangerouslySetInnerHTML: PropTypes.instanceOf(Object),
  label: PropTypes.string.isRequired,
  invisible: PropTypes.bool,
  inverse: PropTypes.bool,
  primary: PropTypes.bool,
  secondary: PropTypes.bool,
  info: PropTypes.bool,
  warning: PropTypes.bool,
  danger: PropTypes.bool,
  disabled: PropTypes.bool,
  isRound: PropTypes.bool,
  default: PropTypes.bool,
  loading: PropTypes.bool,
  href: PropTypes.string,
  to: PropTypes.string,
  ref: PropTypes.string,
  className: PropTypes.string,
  target: PropTypes.string,
  rel: PropTypes.string,
  type: PropTypes.string,
  noClass: PropTypes.bool,
  active: PropTypes.bool,
  children: PropTypes.oneOf([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
};

Button.defaultProps = {
  onClick: () => {},
  dangerouslySetInnerHTML: null,
  invisible: false,
  inverse: false,
  primary: false,
  secondary: false,
  info: false,
  warning: false,
  danger: false,
  disabled: false,
  isRound: bvVar('isTonality'),
  default: false,
  loading: false,
  href: null,
  to: null,
  ref: null,
  className: '',
  target: null,
  rel: null,
  type: null,
  noClass: false,
  active: false,
  children: '',
};

export default Button;
